/**
 * THE BOOKING OUTCOME, TAKEN FROM THE SERVER AND NEVER FROM THE SCREEN.
 *
 * Product approved four outcome values. The dangerous way to produce them is
 * the obvious one: watch which screen rendered, or which step the flow reached,
 * or whether the mutation's HTTP status was 200. Every one of those is a
 * SECOND author for a fact the backend already owns, and the first time the two
 * disagree the report says something the appointment does not.
 *
 * `BookingOutcome.tsx` already carries this rule for copy — "THE STATUS PILL IS
 * THE SERVER'S WORDS … a client deriving its own label is a second author for
 * the same fact". This is the same rule for measurement.
 *
 * So the mapping's only input is `BookingState`, which is produced by
 * `useBooking` from the mutation's own typed result and by
 * `isAuthorizedPilotOutcome`, which has already refused any server answer this
 * cohort cannot describe.
 *
 * WHAT IS DELIBERATELY NOT MAPPED, and why silence is the right answer:
 *
 *   identity-unavailable   nothing was ever sent. There is no outcome.
 *   cannot-start           the attempt could not be constructed; nothing sent.
 *   unauthorized           the server answered something this cohort cannot
 *                          describe. `BookingOutcome` refuses to render copy
 *                          for it; a report must not invent a category either.
 *   account-required,      real, typed refusals — and none of them is one of
 *   contact-invalid,       Product's four. Bucketing them into `unavailable`
 *   settings-changed,      would be inventing taxonomy; bucketing them into
 *   refused                `indeterminate` would be worse, because that value
 *                          means "we do not know whether it committed" and
 *                          these are all definite.
 *   already-booked         the booking EXISTS, which makes `confirmed`
 *                          tempting. It is still deriving: the server told us
 *                          a duplicate was refused, not that this attempt
 *                          confirmed. Out of cohort in September anyway.
 *
 * There is no `other` value and no fallback. An unmapped state is not measured,
 * which is the same rule the route classifier follows.
 */
import type { BookingState } from "../features/booking/useBooking";
import type { Outcome } from "./events";

export function outcomeFor(state: BookingState): Outcome | null {
  switch (state.status) {
    case "done":
      // The server's own pairing, already proved consistent upstream.
      return state.result.kind === "confirmed" ? "confirmed" : "requested";

    case "uncertain":
      // The request may have committed and we did not hear the answer. This is
      // exactly what `indeterminate` is for, and the only state that is.
      return "indeterminate";

    case "failed":
      switch (state.failure.kind) {
        case "slot-unavailable":
        case "hold-gone":
          // Definite server refusals meaning the time cannot be had.
          return "unavailable";
        case "lost":
          // No answer. Same epistemic position as `uncertain`.
          return "indeterminate";
        default:
          return null;
      }

    default:
      return null;
  }
}
